import React, { memo, useCallback } from 'react';

import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import { CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum, LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import { groupFieldsFunction } from './LoanActionsGroup.utils';

const FormLoanEditorActions = [
  LoanEditorActionsEnum.SEND_FORM_BUNDLE,
  LoanEditorActionsEnum.SEND_FORM,
];

const LoanActionRow = ({
  index,
  rootPath,
  options,
  isDisabled,
  isEditAllowed,
  isSingleItem,
  label = '',
  itemType = '',
  withPreview = false,
}) => {
  const { form, setInForm, setMultipleInForm, setValidationErrors } = useFormContext();

  const formKey = `${rootPath}[${index}]`;
  const isTestFieldName = `${formKey}.isTest`;
  const requiresApprovalFieldName = `${formKey}.requiresApproval`;
  const enablePreviewFieldName = `${formKey}.enablePreview`;
  const formsFieldName = `${formKey}.forms`;
  const formForPreviewFieldName = `${formKey}.formForPreview`;

  const forms = _.get(form, rootPath);
  const isTestFieldValue = _.get(form, isTestFieldName);
  const requiresApprovalFieldValue = _.get(form, requiresApprovalFieldName);
  const enablePreviewFieldValue = _.get(form, enablePreviewFieldName);
  const formsFieldValue = _.get(form, formsFieldName);

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];
    newForms.splice(index, 1);
    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  const onIsTestChange = useCallback(
    e => {
      setInForm(isTestFieldName, e.target.checked);
    },
    [isTestFieldName, setInForm],
  );

  const onRequiresApprovalChange = useCallback(
    e => {
      setInForm(requiresApprovalFieldName, e.target.checked);
    },
    [requiresApprovalFieldName, setInForm],
  );

  const onEnablePreviewChange = useCallback(
    e => {
      setInForm(enablePreviewFieldName, e.target.checked);
    },
    [enablePreviewFieldName, setInForm],
  );

  const isFormSelected = FormLoanEditorActions.includes(itemType) && !!_.get(form, formKey);
  const isFormBundle = itemType === LoanEditorActionsEnum.SEND_FORM_BUNDLE;

  const onChange = useCallback(() => {
    if (isFormSelected) {
      setMultipleInForm({
        [isTestFieldName]: isTestFieldValue,
        [requiresApprovalFieldName]: requiresApprovalFieldValue,
        [formForPreviewFieldName]: null,
        [enablePreviewFieldName]: enablePreviewFieldValue,
      });
    }
  }, [
    isFormSelected,
    isTestFieldName,
    isTestFieldValue,
    requiresApprovalFieldName,
    requiresApprovalFieldValue,
    formForPreviewFieldName,
    enablePreviewFieldName,
    enablePreviewFieldValue,
    setMultipleInForm,
  ]);

  return (
    <div className={wrapperClassname}>
      <div className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          groupBy={groupFieldsFunction}
          label={label}
          name={formKey}
          options={options}
          type={FieldTypesEnum.SELECT}
          onChange={onChange}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(formKey)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </div>

      {isFormSelected && (
        <CheckboxCDS
          data-testid={loanManagementTestIds.LOAN_MANAGEMENT_FORM_IS_TEST_CHECKBOX}
          disabled={isDisabled || !isEditAllowed}
          label="Is Test?"
          name={isTestFieldName}
          value={isTestFieldValue}
          onChange={onIsTestChange}
        />
      )}

      <CheckboxCDS
        data-testid={loanManagementTestIds.LOAN_MANAGEMENT_FORM_REQUIRES_APPROVAL_CHECKBOX}
        disabled={isDisabled || !isEditAllowed}
        isHidden={!(isFormBundle && isFormSelected)}
        label="Requires approval"
        name={requiresApprovalFieldName}
        value={requiresApprovalFieldValue}
        onChange={onRequiresApprovalChange}
      />

      {withPreview && isFormSelected && (
        <CheckboxCDS
          data-testid={loanManagementTestIds.LOAN_MANAGEMENT_FORM_ENABLE_PREVIEW_CHECKBOX}
          disabled={isDisabled || !isEditAllowed}
          label="Enable preview before sending?"
          name={enablePreviewFieldName}
          value={enablePreviewFieldValue}
          onChange={onEnablePreviewChange}
        />
      )}

      {withPreview && isFormSelected && enablePreviewFieldValue && (
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          groupBy={groupFieldsFunction}
          label="Form for preview"
          name={formForPreviewFieldName}
          options={formsFieldValue}
          type={FieldTypesEnum.SELECT}
        />
      )}
    </div>
  );
};

LoanActionRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  label: PropTypes.string,
  itemType: PropTypes.string,
  withPreview: PropTypes.bool,
};

export default memo(LoanActionRow);
